import React, { useState, useEffect, useMemo } from 'react';
import { getCurrentExchangeRate } from '../../utils/currencyUtils';
import { CheckIcon } from '@heroicons/react/20/solid';

const PricingPage = () => {
  const [frequency, setFrequency] = useState('monthly');
  const [currency, setCurrency] = useState('CAD');
  const [cadToUsdRate, setCadToUsdRate] = useState(0.74); // Default fallback rate
  const [isLoadingRate, setIsLoadingRate] = useState(true);
  
  // Fetch current exchange rate on component mount
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        setIsLoadingRate(true);
        const rate = await getCurrentExchangeRate();
        setCadToUsdRate(rate);
      } catch (error) {
        console.error('Error fetching exchange rate:', error);
        // Keep using the fallback rate if there's an error
      } finally {
        setIsLoadingRate(false);
      }
    };
    
    fetchExchangeRate();
    
    // Refresh exchange rate every hour if the component remains mounted
    const intervalId = setInterval(fetchExchangeRate, 60 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  const tiers = useMemo(() => [
    {
      name: 'Basic',
      id: 'tier-basic',
      href: '/register',
      price: { 
        monthly: { 
          CAD: '$99', 
          USD: `$${Math.round(99 * cadToUsdRate)}` 
        }, 
        annually: { 
          CAD: `$${Math.round(99 * 12 * 0.85)}`, 
          USD: `$${Math.round(99 * 12 * 0.85 * cadToUsdRate)}` 
        } 
      },
      description: 'Perfect for small businesses or individual resellers',
      features: ['Up to 1,000 monthly scans', 'Unlimited users', 'AI item descriptions', 'Barcode scanning', 'Spreadsheet exports', 'Priority support', 'Multi-location support'],
      mostPopular: false,
      buttonText: 'Sign up',
      buttonVariant: 'outline',
    },
    {
      name: 'Professional',
      id: 'tier-professional',
      href: '/register',
      price: { 
        monthly: { 
          CAD: '$189', 
          USD: `$${Math.round(189 * cadToUsdRate)}` 
        }, 
        annually: { 
          CAD: `$${Math.round(189 * 12 * 0.85)}`, 
          USD: `$${Math.round(189 * 12 * 0.85 * cadToUsdRate)}` 
        } 
      },
      description: 'Ideal for growing businesses with medium-sized inventory needs',
      features: ['Up to 2,000 monthly scans', 'Unlimited users', 'AI item descriptions', 'Barcode scanning', 'Spreadsheet exports', 'Priority support', 'Multi-location support'],
      mostPopular: true,
      buttonText: 'Get Started Today',
      buttonVariant: 'solid',
    },
    {
      name: 'Enterprise',
      id: 'tier-enterprise',
      href: '/register',
      price: { 
        monthly: { 
          CAD: '$249', 
          USD: `$${Math.round(249 * cadToUsdRate)}` 
        }, 
        annually: { 
          CAD: `$${Math.round(249 * 12 * 0.85)}`, 
          USD: `$${Math.round(249 * 12 * 0.85 * cadToUsdRate)}` 
        } 
      },
      description: 'For large businesses with advanced requirements',
      features: ['Up to 3,000 monthly scans', 'Unlimited users', 'AI item descriptions', 'Barcode scanning', 'Spreadsheet exports', 'Priority support', 'Multi-location support'],
      mostPopular: false,
      buttonText: 'Sign up',
      buttonVariant: 'outline',
    },
  ], [cadToUsdRate]);

  // Additional scan packages with dynamic USD conversion
  const additionalScans = useMemo(() => {
    const basicCADPrices = [45, 85, 155];
    const professionalCADPrices = [80, 175, 325];
    const enterpriseCADPrices = [120, 275, 500];
    
    return {
      basic: [
        { 
          scans: 500, 
          price: { 
            CAD: `$${basicCADPrices[0]}`, 
            USD: `$${Math.round(basicCADPrices[0] * cadToUsdRate)}` 
          } 
        },
        { 
          scans: 1000, 
          price: { 
            CAD: `$${basicCADPrices[1]}`, 
            USD: `$${Math.round(basicCADPrices[1] * cadToUsdRate)}` 
          } 
        },
        { 
          scans: 2000, 
          price: { 
            CAD: `$${basicCADPrices[2]}`, 
            USD: `$${Math.round(basicCADPrices[2] * cadToUsdRate)}` 
          } 
        }
      ],
      professional: [
        { 
          scans: 1000, 
          price: { 
            CAD: `$${professionalCADPrices[0]}`, 
            USD: `$${Math.round(professionalCADPrices[0] * cadToUsdRate)}` 
          } 
        },
        { 
          scans: 2500, 
          price: { 
            CAD: `$${professionalCADPrices[1]}`, 
            USD: `$${Math.round(professionalCADPrices[1] * cadToUsdRate)}` 
          } 
        },
        { 
          scans: 5000, 
          price: { 
            CAD: `$${professionalCADPrices[2]}`, 
            USD: `$${Math.round(professionalCADPrices[2] * cadToUsdRate)}` 
          } 
        }
      ],
      enterprise: [
        { 
          scans: 2000, 
          price: { 
            CAD: `$${enterpriseCADPrices[0]}`, 
            USD: `$${Math.round(enterpriseCADPrices[0] * cadToUsdRate)}` 
          } 
        },
        { 
          scans: 5000, 
          price: { 
            CAD: `$${enterpriseCADPrices[1]}`, 
            USD: `$${Math.round(enterpriseCADPrices[1] * cadToUsdRate)}` 
          } 
        },
        { 
          scans: 10000, 
          price: { 
            CAD: `$${enterpriseCADPrices[2]}`, 
            USD: `$${Math.round(enterpriseCADPrices[2] * cadToUsdRate)}` 
          } 
        }
      ]
    };
  }, [cadToUsdRate]);

  return (
    <div>
      <div className="pt-4 pb-16 text-center">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4">
          Pricing plans for businesses of any size
        </h1>
        <p className="text-xl text-[#D4D1EC] max-w-3xl mx-auto">
          Choose the perfect plan for your inventory management needs. All plans include our core features, with advanced capabilities available in higher tiers. <br/>*All plans start with a 14 day Free Trial.
        </p>
        
        {/* Pricing Toggle */}
        <div className="mt-12 flex justify-center">
          <div className="bg-[#134448] rounded-full p-1 inline-flex">
            <button
              type="button"
              className={`${frequency === 'monthly' ? 'bg-[#1ABC9D] shadow-md text-[#0C0F19]' : 'text-[#1ABC9D]'} px-6 py-2 text-sm font-medium rounded-full focus:outline-none`}
              onClick={() => setFrequency('monthly')}
            >
              Monthly Billing
            </button>
            <button
              type="button"
              className={`${frequency === 'annually' ? 'bg-[#1ABC9D] shadow-md text-[#0C0F19]' : 'text-[#1ABC9D]'} px-6 py-2 text-sm font-medium rounded-full focus:outline-none`}
              onClick={() => setFrequency('annually')}
            >
              Annual Billing
            </button>
          </div>
        </div>
        
        {/* Currency Toggle */}
        <div className="mt-4 flex justify-center">
          <div className="bg-[#134448] rounded-full p-1 inline-flex">
            <button
              type="button"
              className={`${currency === 'CAD' ? 'bg-[#1ABC9D] shadow-md text-[#0C0F19]' : 'text-[#1ABC9D]'} px-6 py-2 text-sm font-medium rounded-full focus:outline-none`}
              onClick={() => setCurrency('CAD')}
            >
              CAD
            </button>
            <button
              type="button"
              className={`${currency === 'USD' ? 'bg-[#1ABC9D] shadow-md text-[#0C0F19]' : 'text-[#1ABC9D]'} px-6 py-2 text-sm font-medium rounded-full focus:outline-none`}
              onClick={() => setCurrency('USD')}
            >
              USD
            </button>
          </div>
        </div>
        
        {/* Exchange Rate Display */}
        <div className="mt-2 text-center">
          {isLoadingRate ? (
            <div className="text-sm text-[#1ABC9D] flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#1ABC9D]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Updating exchange rate...
            </div>
          ) : (
            <div className="text-sm text-[#A5A5BF]">
              Current rate: 1 CAD = {cadToUsdRate.toFixed(2)} USD
            </div>
          )}
        </div>
      </div>

      {/* Pricing Tables */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 max-w-7xl mx-auto">
        {tiers.map((tier) => (
          <div
            key={tier.id}
            className={`rounded-2xl ${
              tier.mostPopular
                ? 'border-2 border-[#1ABC9D] bg-[#101729] shadow-lg'
                : 'border border-gray-800 bg-[#101729]'
            } p-8 relative`}
          >
            {tier.mostPopular && (
              <div className="absolute top-4 right-6">
                <span className="inline-flex items-center rounded-full bg-[#134448] px-4 py-1 text-xs font-medium text-[#1ABC9D]">
                  Most Popular
                </span>
              </div>
            )}
            <div>
              <h3 className="text-xl font-semibold text-white">{tier.name}</h3>
              <p className="mt-1 text-sm text-[#A5A5BF]">{tier.description}</p>
              <p className="mt-6 flex items-baseline">
                <span className="text-[56px] font-bold tracking-tight leading-[110%] tracking-[-0.02em] text-white" style={{fontFamily: 'Inter, sans-serif'}}>
                  {tier.price[frequency][currency]}
                </span>
                <span className="ml-1 text-[18px] font-medium leading-[140%] tracking-[-0.005em] text-white" style={{fontFamily: 'Inter, sans-serif'}}>
                  {frequency === 'monthly' ? '/ month' : '/ year'} {currency}
                </span>
              </p>
            </div>
            
            <a
              href={tier.href}
              className={`mt-6 block w-full rounded-full py-3 px-3 text-center text-sm font-semibold ${
                tier.mostPopular
                  ? 'bg-[#1ABC9D] text-[#0C0F19] hover:bg-[#1DC9A0] border-t border-[#66E0C2]'
                  : 'bg-[#101729] text-[#1ABC9D] hover:bg-[#101729] border border-[#1ABC9D]'
              }`}
            >
              {tier.buttonText}
            </a>
            
            <ul role="list" className="mt-8 space-y-4 text-sm text-[#A5A5BF]">
              {tier.features.map((feature) => (
                <li key={feature} className="flex items-center">
                  <CheckIcon className="h-5 w-5 flex-shrink-0 text-[#1ABC9D]" aria-hidden="true" />
                  <span className="ml-3">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Additional Scans Section */}
      <div className="mt-24 text-center">
        <h2 className="text-3xl font-bold text-white">Need more scans? We've got you.</h2>
        <p className="mt-4 text-xl text-[#D4D1EC] max-w-3xl mx-auto">
          Top up with one-time scan packs on your terms, with no impact to your current subscription. Perfect for busy listing days, big sales, or scaling teams.
        </p>
        
        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-3 max-w-7xl mx-auto">
          {/* Basic Add-ons */}
          <div className="rounded-2xl border border-gray-800 bg-[#103B3F] p-8 text-left">
            <h3 className="text-xl font-semibold text-white mb-4">Basic</h3>
            <ul className="space-y-4">
              {additionalScans.basic.map((pack, index) => (
                <li key={index} className="flex justify-between items-center text-white font-bold border-b border-gray-800 pb-3">
                  <span>+{pack.scans.toLocaleString()} scans</span>
                  <span>{pack.price[currency]}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Professional Add-ons */}
          <div className="rounded-2xl border-2 border-[#1ABC9D] bg-[#103B3F] p-8 text-left relative">
            <h3 className="text-xl font-semibold text-white mb-4">Professional</h3>
            <ul className="space-y-4">
              {additionalScans.professional.map((pack, index) => (
                <li key={index} className="flex justify-between items-center text-white font-bold border-b border-gray-800 pb-3">
                  <span>+{pack.scans.toLocaleString()} scans</span>
                  <span>{pack.price[currency]}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Enterprise Add-ons */}
          <div className="rounded-2xl border border-gray-800 bg-[#103B3F] p-8 text-left">
            <h3 className="text-xl font-semibold text-white mb-4">Enterprise</h3>
            <ul className="space-y-4">
              {additionalScans.enterprise.map((pack, index) => (
                <li key={index} className="flex justify-between items-center text-white font-bold border-b border-gray-800 pb-3">
                  <span>+{pack.scans.toLocaleString()} scans</span>
                  <span>{pack.price[currency]}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Call to Action Section - Same structure as homepage */}
      <div className="relative z-10 bg-[#103B3F] w-full py-16 md:py-24 mt-12 md:mt-16">
        <div className="px-4 sm:px-6 lg:px-8 mx-auto max-w-3xl text-center">
          <h3 className="text-white text-2xl md:text-3xl lg:text-4xl font-semibold mb-4">
            Join other auction companies and resellers using RocketScanner
          </h3>
          <p className="text-white text-base md:text-lg leading-relaxed mb-8">
            With RocketScanner, auction teams manage inventory with ease —<br />and stay focused on what matters most.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
            <a 
              href="/register" 
              className="flex items-center justify-center px-8 py-3 rounded-full bg-[#1DC9A0] font-bold text-[#0B0F19] shadow-md border-t border-[#66E0C2] hover:bg-[#66E0C2] transition-colors"
            >
              Get Started
            </a>
            <a 
              href="/about" 
              className="flex items-center justify-center text-white font-bold hover:text-[#AAA1FD] transition-colors"
            >
              Learn More
              <svg className="ml-2" width="25" height="22" viewBox="0 0 25 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.5 1L24 11M24 11L13.5 21M24 11H1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PricingPage;
